import { apiUrl } from "@/lib/api-base";
import { getAuthToken } from "@/lib/auth";

const SYNC_KEYS = new Set([
  "seo-nexus-site-settings",
  "seo-nexus-site-settings-sync-meta",
  "seo-nexus-keyword-center-plan-v2",
  "seo-nexus-content-factory-plans",
  "seo-nexus-content-factory-pending-articles",
  "seo-nexus-content-factory-selected-site",
  "seo-nexus-indexing-submit-config",
  "seo-nexus-builder-domains",
  "seo-nexus-builder-domain-draft",
  "seo-nexus-built-sites",
  "seo-nexus-custom-sites",
  "seo-nexus-builder-agent-memory",
  "seo-nexus-uploaded-templates",
  "seo-nexus-deleted-templates",
  "seo-nexus-pinned-templates",
  "seo-nexus-favorite-templates",
  "seo-nexus-hidden-sites",
  "seo-nexus-site-overrides",
  "seo-nexus-site-groups",
  "seo-nexus-customer-management-v1"
]);

const META_KEY = "__seo_nexus_client_storage_sync_meta__";
const INSTALL_FLAG = "__seoNexusClientStorageSyncInstalled__";
const ACTIVE_USER_KEY = "__seo_nexus_active_user_id__";
const nativeSetItem = Storage.prototype.setItem;
const nativeRemoveItem = Storage.prototype.removeItem;
const nativeClear = Storage.prototype.clear;

type SyncMeta = Record<string, string>;

type RemoteItem = {
  value?: string;
  updatedAt?: string;
};

type RemoteResponse = {
  ok?: boolean;
  data?: {
    items?: Record<string, RemoteItem>;
  };
};

function readMeta(): SyncMeta {
  try {
    const raw = window.localStorage.getItem(META_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeMeta(meta: SyncMeta) {
  window.localStorage.setItem(META_KEY, JSON.stringify(meta));
}

function nowIso() {
  return new Date().toISOString();
}

function isSyncKey(key: string) {
  return SYNC_KEYS.has(key);
}

function hasLocalSyncedClientStorage() {
  return Array.from(SYNC_KEYS).some(key => window.localStorage.getItem(key) !== null) ||
    window.localStorage.getItem(META_KEY) !== null;
}

function authHeaders() {
  const token = getAuthToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "Content-Type": "application/json"
  };
}

function postItems(items: Array<{ key: string; value: string | null; updatedAt?: string }>) {
  const token = getAuthToken();
  if (!token || !items.length) return;
  window.fetch(apiUrl("/client-storage"), {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ items }),
    keepalive: JSON.stringify({ items }).length < 60000
  }).catch(() => {});
}

export async function flushClientStorageSync(keys: string[] = Array.from(SYNC_KEYS)) {
  const token = getAuthToken();
  if (!token || typeof window === "undefined") return false;
  const meta = readMeta();
  const updatedAt = nowIso();
  const items = Array.from(new Set(keys))
    .filter(isSyncKey)
    .map(key => {
      const value = window.localStorage.getItem(key);
      return value === null ? null : { key, value, updatedAt: meta[key] || updatedAt };
    })
    .filter((item): item is { key: string; value: string; updatedAt: string } => Boolean(item));
  if (!items.length) return false;
  const response = await window.fetch(apiUrl("/client-storage"), {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ items })
  });
  return response.ok;
}

function scheduleSync(key: string, value: string | null) {
  if (!isSyncKey(key)) return;
  const meta = readMeta();
  const updatedAt = nowIso();
  meta[key] = updatedAt;
  writeMeta(meta);
  window.clearTimeout((scheduleSync as unknown as { timer?: number }).timer);
  (scheduleSync as unknown as { queue?: Map<string, { value: string | null; updatedAt: string }> }).queue ??= new Map();
  const queue = (scheduleSync as unknown as { queue: Map<string, { value: string | null; updatedAt: string }> }).queue;
  queue.set(key, { value, updatedAt });
  (scheduleSync as unknown as { timer?: number }).timer = window.setTimeout(() => {
    const payload = Array.from(queue.entries()).map(([queuedKey, item]) => ({
      key: queuedKey,
      value: item.value,
      updatedAt: item.updatedAt
    }));
    queue.clear();
    postItems(payload);
  }, 700);
}

function patchLocalStorage() {
  const storage = window.localStorage;

  Storage.prototype.setItem = function setItemWithSync(key: string, value: string) {
    nativeSetItem.call(this, key, value);
    scheduleSync(key, value);
  };

  Storage.prototype.removeItem = function removeItemWithSync(key: string) {
    nativeRemoveItem.call(this, key);
    scheduleSync(key, null);
  };

  Storage.prototype.clear = function clearWithSync() {
    const removedKeys = Array.from(SYNC_KEYS).filter(key => storage.getItem(key) !== null);
    nativeClear.call(this);
    removedKeys.forEach(key => scheduleSync(key, null));
  };
}

export async function hydrateClientStorageFromServer() {
  const token = getAuthToken();
  if (!token) return;

  const response = await window.fetch(apiUrl("/client-storage"), {
    method: "GET",
    headers: authHeaders()
  }).catch(() => null);
  if (!response?.ok) return;

  const body = await response.json().catch(() => null) as RemoteResponse | null;
  const remoteItems = body?.data?.items || {};
  const meta = readMeta();
  const localPush: Array<{ key: string; value: string; updatedAt?: string }> = [];
  for (const key of SYNC_KEYS) {
    const localValue = window.localStorage.getItem(key);
    const localUpdatedAt = meta[key] || "";
    const remote = remoteItems[key];
    const remoteValue = typeof remote?.value === "string" ? remote.value : null;
    const remoteUpdatedAt = remote?.updatedAt || "";

    if (remoteValue !== null && (!localValue || remoteUpdatedAt > localUpdatedAt)) {
      nativeSetItem.call(window.localStorage, key, remoteValue);
      meta[key] = remoteUpdatedAt || nowIso();
      continue;
    }

    if (localValue !== null && (!remoteValue || localUpdatedAt > remoteUpdatedAt)) {
      const updatedAt = localUpdatedAt || nowIso();
      meta[key] = updatedAt;
      localPush.push({ key, value: localValue, updatedAt });
    }
  }

  writeMeta(meta);
  if (localPush.length) postItems(localPush);
}

export function prepareClientStorageForUser(userId: string) {
  if (typeof window === "undefined") return;
  const nextUserId = String(userId || "").trim();
  if (!nextUserId) return;
  const previousUserId = window.localStorage.getItem(ACTIVE_USER_KEY) || "";
  if ((previousUserId && previousUserId !== nextUserId) || (!previousUserId && hasLocalSyncedClientStorage())) {
    for (const key of SYNC_KEYS) {
      nativeRemoveItem.call(window.localStorage, key);
    }
    nativeRemoveItem.call(window.localStorage, META_KEY);
  }
  nativeSetItem.call(window.localStorage, ACTIVE_USER_KEY, nextUserId);
}

export function clearLocalSyncedClientStorage() {
  if (typeof window === "undefined") return;
  for (const key of SYNC_KEYS) {
    nativeRemoveItem.call(window.localStorage, key);
  }
  nativeRemoveItem.call(window.localStorage, META_KEY);
  nativeRemoveItem.call(window.localStorage, ACTIVE_USER_KEY);
}

export function installClientStorageSync() {
  if (typeof window === "undefined") return;
  const globalWindow = window as Window & { [INSTALL_FLAG]?: boolean };
  if (globalWindow[INSTALL_FLAG]) return;
  globalWindow[INSTALL_FLAG] = true;
  patchLocalStorage();
  void hydrateClientStorageFromServer();
  window.addEventListener("storage", event => {
    if (event.key && isSyncKey(event.key)) scheduleSync(event.key, event.newValue);
  });
}
